import { EarningsInsight, EarningsItemBase, Entity } from '@api';
import { Icon } from '@design/components/icon/Icon';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { generatePath } from '@helpers';
import { config } from '@terminal/config/app.config';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { EarningsInsightPill } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-pill/EarningsInsightPill';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import styles from './EarningsInsightSection.module.scss';
import { PreviewContent } from './PreviewContent';
import { RecapContent } from './RecapContent';
import { getRecapTitle } from './utils';

export interface EarningsInsightSectionProps {
  entity: Entity;
  earningsData?: EarningsItemBase;
  earningsInsight?: EarningsInsight;
  isLoading: boolean;
  isPreview: boolean;
}

export const EarningsInsightSection = ({
  entity,
  earningsData,
  earningsInsight,
  isLoading,
  isPreview,
}: EarningsInsightSectionProps) => {
  const { t } = useTranslation(['widget']);
  const navigate = useNavigate();

  if (!earningsData) {
    return null;
  }

  const handleExpandClick = () => {
    const requestPath = generatePath({
      basePath: appPaths.assetOverview,
      params: {
        [queryKeys.assetOverviewAsset]: entity.tag,
        [queryKeys.assetOverviewTab]: 'earnings',
      },
    });
    navigate(requestPath);
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <h3 className={styles.title}>
            {isPreview
              ? t('widget:assetOverview.earningsSection.upcomingEarnings')
              : getRecapTitle(
                  t,
                  earningsData.fiscal_period,
                  earningsData.fiscal_year
                )}
          </h3>
          {earningsInsight && (
            <EarningsInsightPill
              isPreview={isPreview}
              entityTag={entity.tag}
              earningsInsight={earningsInsight}
              source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
              isClickable={!config.app.isTeamsApp}
            />
          )}
        </div>
        <WebOnlyFeat>
          <Tooltip
            label={t('widget:assetOverview.viewMore')}
            inPortal
            trigger={[TooltipTrigger.Hover]}
          >
            <button
              type="button"
              className={styles.expandButton}
              onClick={handleExpandClick}
              aria-label={t('widget:assetOverview.viewMore')}
            >
              <Icon className={styles.expandIcon} iconName="Expand" size={16} />
            </button>
          </Tooltip>
        </WebOnlyFeat>
      </div>
      {isLoading ? (
        <SkeletonLoader
          areas={[
            { styles: { height: '100px', width: '100%', borderRadius: '8px' } },
          ]}
        />
      ) : (
        <div className={styles.content}>
          {isPreview ? (
            <PreviewContent earningsData={earningsData} />
          ) : (
            <RecapContent earningsData={earningsData} entity={entity} />
          )}
        </div>
      )}
    </div>
  );
};
